import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { FormField, Input } from '../../components/ui/FormField.js';
import { useAbility } from '../../context/AbilityContext.js';
import { api } from '../../services/api.js';

const SIDES: { key: string; label: string }[] = [
  { key: 'ic_front', label: 'NRIC front' },
  { key: 'ic_back', label: 'NRIC back' },
  { key: 'driving_licence_front', label: 'Driving licence front' },
  { key: 'driving_licence_back', label: 'Driving licence back' },
  { key: 'passport_front', label: 'Passport front' },
  { key: 'passport_back', label: 'Passport back' },
  { key: 'selfie', label: 'Selfie' },
  { key: 'student_card_front', label: 'Student card front' },
  // 312 (#538): no student card BACK — it can carry ATM card details, and is no longer collected.
];

interface IdentitySummary {
  status: 'pending' | 'submitted' | 'approved' | 'rejected' | 'resubmitted';
  rejection_reason?: string;
  sides: Record<string, boolean>;
  /** 209 (#305): reviewer-visible/editable, alongside the images they describe. */
  student_id?: string;
  driving_licence_expires_at?: string;
  /** 350 (#953): when a rejected identity's documents are deleted; null when none are waiting. */
  images_delete_after?: string | null;
}

/** 350: a date as Malaysia sees it, e.g. "2 Nov 2026". */
function mytDay(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    timeZone: 'Asia/Kuala_Lumpur',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** Admin panel to review a user's identity documents and set verification
 * status. Renders nothing unless the current admin can manage:'User' (US3). */
export function IdentityReview({ userId }: { userId: string }) {
  const ability = useAbility();
  const canManage = ability.can('manage', 'User');
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');

  const { data } = useQuery({
    queryKey: ['identity-review', userId],
    queryFn: () => api.get<IdentitySummary>(`/users/${userId}/identity`),
    enabled: canManage,
  });

  // 209 (#305): prefilled from the fetched record, editable, included in the decision request
  // only when actually CHANGED from what was loaded — the "omitted key = untouched" contract the
  // backend route already uses, and what keeps an unmodified decision request unchanged.
  const [studentId, setStudentId] = useState('');
  const [licenceExpiry, setLicenceExpiry] = useState('');
  const [detailsPrefilled, setDetailsPrefilled] = useState(false);

  useEffect(() => {
    if (data && !detailsPrefilled) {
      setStudentId(data.student_id ?? '');
      setLicenceExpiry(data.driving_licence_expires_at?.slice(0, 10) ?? '');
      setDetailsPrefilled(true);
    }
  }, [data, detailsPrefilled]);

  const review = useMutation({
    mutationFn: (body: {
      status: 'approved' | 'rejected';
      rejection_reason?: string;
      student_id?: string;
      driving_licence_expires_at?: string;
    }) => api.patch(`/users/${userId}/identity/status`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['identity-review', userId] }),
  });

  function decide(status: 'approved' | 'rejected') {
    const body: {
      status: 'approved' | 'rejected';
      rejection_reason?: string;
      student_id?: string;
      driving_licence_expires_at?: string;
    } = { status };
    if (status === 'rejected') body.rejection_reason = reason.trim();
    if (studentId !== (data?.student_id ?? '')) body.student_id = studentId;
    const loadedExpiry = data?.driving_licence_expires_at?.slice(0, 10) ?? '';
    if (licenceExpiry !== loadedExpiry) body.driving_licence_expires_at = licenceExpiry;
    review.mutate(body);
  }

  if (!canManage) return null;

  const sides = data?.sides ?? {};

  return (
    <Card>
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-steel uppercase tracking-wide">
          Identity documents
        </h2>
        <span className="text-xs font-medium text-ink">{data?.status ?? 'pending'}</span>
      </div>

      {data?.status === 'rejected' && data.rejection_reason && (
        <p className="mb-3 text-sm text-red-600">Rejected: {data.rejection_reason}</p>
      )}
      {data?.status === 'rejected' && data.images_delete_after && (
        <p data-testid="identity-delete-after" className="mb-3 text-xs text-steel">
          Documents kept for review until {mytDay(data.images_delete_after)}, then deleted.
        </p>
      )}

      <ul className="mb-4 grid gap-2 sm:grid-cols-2">
        {SIDES.map(({ key, label }) => (
          <li
            key={key}
            className="flex items-center justify-between rounded-xl border border-hairline shadow-[0px_1px_2px_0px_rgba(0,30,43,0.04)] px-3 py-2 text-sm"
          >
            <span className="text-ink">{label}</span>
            {sides[key] ? (
              <button
                type="button"
                className="text-xs font-medium text-brand-blue hover:underline"
                onClick={async () => {
                  const url = await api.getBlobUrl(`/users/${userId}/identity/${key}/file`);
                  window.open(url, '_blank', 'noopener');
                  // Release the object URL once the new tab has had time to load it.
                  setTimeout(() => URL.revokeObjectURL(url), 60_000);
                }}
              >
                View
              </button>
            ) : (
              <span className="text-xs text-steel">Not provided</span>
            )}
          </li>
        ))}
      </ul>

      {/* 209 (#305): visible, editable, alongside the images they describe. */}
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-start">
        <div className="flex-1">
          <FormField label="Student ID" name="identity-review-student-id" hint="Optional">
            <Input
              type="text"
              id="identity-review-student-id"
              data-testid="identity-review-student-id"
              value={studentId}
              onChange={(e) => setStudentId(e.currentTarget.value)}
            />
          </FormField>
        </div>
        <div className="flex-1">
          <FormField label="Driving licence expiry date" name="identity-review-licence-expiry">
            <Input
              type="date"
              id="identity-review-licence-expiry"
              data-testid="identity-review-licence-expiry"
              value={licenceExpiry}
              onChange={(e) => setLicenceExpiry(e.currentTarget.value)}
            />
          </FormField>
        </div>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <Input
          type="text"
          placeholder="Rejection reason"
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          className="flex-1"
        />
        <div className="flex gap-2">
          <Button size="sm" loading={review.isPending} onClick={() => decide('approved')}>
            Approve
          </Button>
          <Button
            size="sm"
            variant="secondary"
            loading={review.isPending}
            disabled={!reason.trim()}
            onClick={() => decide('rejected')}
          >
            Reject
          </Button>
        </div>
      </div>
      {review.isError && (
        <p className="mt-2 text-sm text-red-500">
          {(review.error as { message?: string }).message ?? 'Failed to update status'}
        </p>
      )}
    </Card>
  );
}
